import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fingerprint, KeyRound, Mail, Trash2, User as UserIcon } from "lucide-react";
import { authApi } from "../lib/api";
import { isPasskeySupported, passkeyApi, registerPasskey } from "../lib/passkey";
import { formatDate } from "../lib/format";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

type Flash = { text: string; ok: boolean } | null;

export default function ProfilePage() {
  const { t } = useI18n();
  return (
    <>
      <header>
        <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("profile.account")}</p>
        <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
          <UserIcon className="w-6 h-6 text-accent" />
          {t("profile.title")}
        </h1>
      </header>

      <ProfileCard />
      <PasswordCard />
      <PasskeysCard />
    </>
  );
}

// ------------- Name + email -------------
function ProfileCard() {
  const { t } = useI18n();
  const { user, emailVerified, refresh } = useAuth();
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);

  // Keep fields in sync once the auth context loads/refreshes.
  useEffect(() => {
    setName(user?.name ?? "");
    setEmail(user?.email ?? "");
  }, [user?.name, user?.email]);

  const emailChanged = email.trim() !== (user?.email ?? "");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFlash(null);
    try {
      await authApi.updateProfile({ name: name.trim(), email: email.trim() });
      await refresh();
      setFlash({
        text: emailChanged
          ? t("profile.savedWithVerify")
          : t("profile.savedSimple"),
        ok: true,
      });
    } catch (err) {
      setFlash({ text: (err as Error).message || t("profile.couldNotSave"), ok: false });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="glass p-6">
      <h2 className="text-lg font-semibold text-parchment mb-2 flex items-center gap-2">
        <UserIcon className="w-5 h-5 text-accent" />
        {t("profile.details.title")}
      </h2>
      <p className="text-parchment-muted text-sm mb-4">
        {t("profile.details.desc")}
      </p>

      <form onSubmit={submit} className="grid gap-4 max-w-lg">
        <div>
          <label className="label block mb-2" htmlFor="profileName">
            {t("profile.name")}
          </label>
          <input
            id="profileName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("profile.namePlaceholder")}
            maxLength={120}
            className="input"
          />
        </div>

        <div>
          <label className="label block mb-2" htmlFor="profileEmail">
            {t("profile.email")}
          </label>
          <div className="relative">
            <Mail className="w-4 h-4 text-parchment-muted absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="profileEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("profile.emailPlaceholder")}
              maxLength={255}
              className="input ps-9"
            />
          </div>
          <p className="mt-2 text-xs text-parchment-muted">
            {emailVerified ? (
              <span className="text-pnl-pos">{t("profile.verified")}</span>
            ) : (
              <span className="text-gold-400">{t("profile.notVerified")}</span>
            )}
          </p>
        </div>

        {flash && (
          <p
            className={
              "rounded-lg px-3 py-2 text-sm " +
              (flash.ok
                ? "bg-emerald-500/10 border border-emerald-500/30 text-pnl-pos"
                : "bg-rose-500/10 border border-rose-500/30 text-pnl-neg")
            }
          >
            {flash.text}
          </p>
        )}

        <div>
          <button type="submit" disabled={busy} className="btn-gold">
            {busy ? t("common.saving") : t("profile.saveChanges")}
          </button>
        </div>
      </form>
    </section>
  );
}

// ------------- Password (no current password required) -------------
function PasswordCard() {
  const { t } = useI18n();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 8 && confirm === password && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setFlash(null);
    try {
      await authApi.updateProfile({
        password,
        password_confirmation: confirm,
      });
      setPassword("");
      setConfirm("");
      setFlash({ text: t("profile.passwordUpdated"), ok: true });
    } catch (err) {
      setFlash({ text: (err as Error).message || t("profile.couldNotChangePassword"), ok: false });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="glass p-6">
      <h2 className="text-lg font-semibold text-parchment mb-2 flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-accent" />
        {t("profile.password.title")}
      </h2>
      <p className="text-parchment-muted text-sm mb-4">
        {t("profile.password.desc")}
      </p>

      <form onSubmit={submit} className="grid gap-4 max-w-lg">
        <div>
          <label className="label block mb-2" htmlFor="newPassword">
            {t("profile.newPassword")}
          </label>
          <input
            id="newPassword"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("profile.newPasswordPlaceholder")}
            autoComplete="new-password"
            maxLength={255}
            className="input"
          />
          {tooShort && (
            <p className="mt-2 text-xs text-pnl-neg">{t("profile.tooShort")}</p>
          )}
        </div>

        <div>
          <label className="label block mb-2" htmlFor="confirmPassword">
            {t("profile.confirmPassword")}
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t("profile.confirmPlaceholder")}
            autoComplete="new-password"
            maxLength={255}
            className="input"
          />
          {mismatch && <p className="mt-2 text-xs text-pnl-neg">{t("profile.mismatch")}</p>}
        </div>

        {flash && (
          <p
            className={
              "rounded-lg px-3 py-2 text-sm " +
              (flash.ok
                ? "bg-emerald-500/10 border border-emerald-500/30 text-pnl-pos"
                : "bg-rose-500/10 border border-rose-500/30 text-pnl-neg")
            }
          >
            {flash.text}
          </p>
        )}

        <div>
          <button type="submit" disabled={!canSubmit} className="btn-gold">
            {busy ? t("profile.updating") : t("profile.updatePassword")}
          </button>
        </div>
      </form>
    </section>
  );
}

// ------------- Passkeys -------------
// (Moved here from Settings — passkeys are an account-security concern, so
// they live alongside password + email on the profile page. i18n keys stay
// under the `settings.*` namespace to preserve existing fa/ar translations.)
function PasskeysCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const passkeyOk = isPasskeySupported();
  const q = useQuery({ queryKey: ["passkeys"], queryFn: () => passkeyApi.list() });

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  async function onRegister() {
    if (!name.trim()) {
      setMsg({ text: t("settings.passkeyNameRequired"), ok: false });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await registerPasskey(name.trim());
      setMsg({ text: t("settings.passkeyRegistered"), ok: true });
      setName("");
      qc.invalidateQueries({ queryKey: ["passkeys"] });
    } catch (e) {
      setMsg({ text: (e as Error).message || t("settings.passkeyRegFailed"), ok: false });
    } finally {
      setBusy(false);
    }
  }

  const destroyM = useMutation({
    mutationFn: (id: number) => passkeyApi.destroy(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passkeys"] }),
  });

  return (
    <section className="glass p-6">
      <h2 className="text-lg font-semibold text-parchment mb-2 flex items-center gap-2">
        <Fingerprint className="w-5 h-5 text-accent" />
        {t("settings.passkeys")}
      </h2>
      <p className="text-parchment-muted text-sm mb-4">
        {t("settings.passkeysHelp")}
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("settings.passkeyNamePlaceholder")}
          className="input flex-1 min-w-[200px]"
          maxLength={80}
        />
        <button
          type="button"
          onClick={() => void onRegister()}
          disabled={!passkeyOk || busy}
          className="btn-gold"
        >
          <Fingerprint className="w-4 h-4" />
          {busy ? t("settings.waitingForPasskey") : t("settings.registerPasskey")}
        </button>
      </div>

      {!passkeyOk && (
        <p className="text-xs text-parchment-muted">
          {t("settings.noWebauthn")}
        </p>
      )}
      {msg && (
        <p
          className={
            "mb-3 rounded-lg px-3 py-2 text-sm " +
            (msg.ok
              ? "bg-emerald-500/10 border border-emerald-500/30 text-pnl-pos"
              : "bg-rose-500/10 border border-rose-500/30 text-pnl-neg")
          }
        >
          {msg.text}
        </p>
      )}

      <ul className="divide-y divide-glass">
        {(q.data?.keys ?? []).map((k) => (
          <li key={k.id} className="py-3 flex items-center gap-3">
            <Fingerprint className="w-4 h-4 text-parchment-muted" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-parchment truncate">{k.name}</p>
              <p className="text-xs text-parchment-muted">{t("settings.addedDate", { date: formatDate(k.created_at) })}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (confirm(t("settings.removePasskeyConfirm", { name: k.name }))) destroyM.mutate(k.id);
              }}
              disabled={destroyM.isPending}
              className="text-parchment-muted hover:text-pnl-neg disabled:opacity-40"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
        {(q.data?.keys.length ?? 0) === 0 && !q.isLoading && (
          <li className="py-6 text-center text-parchment-muted text-sm">
            {t("settings.noPasskeys")}
          </li>
        )}
      </ul>
    </section>
  );
}
